import { z } from 'zod'

import { ModuleTypeEnum } from './module'

/**
 * Enum for permission actions that can be performed on modules
 */
export enum PermissionActionEnum {
  READ = 'READ', // View data
  CREATE = 'CREATE', // Create new records
  UPDATE = 'UPDATE', // Edit existing records
  DELETE = 'DELETE', // Delete records
  MANAGE = 'MANAGE', // Administrative access (change settings)
  APPROVE = 'APPROVE', // Approve/reject items
  SEND = 'SEND', // Dispatch externally visible communications
}

/**
 * Actions every module supports. APPROVE and SEND are workflow-specific —
 * they only exist where the product actually checks them (see
 * MODULE_EXTRA_ACTIONS), so offering them everywhere just creates grants
 * nothing ever reads.
 */
export const BASE_MODULE_ACTIONS: PermissionActionEnum[] = [
  PermissionActionEnum.READ,
  PermissionActionEnum.CREATE,
  PermissionActionEnum.UPDATE,
  PermissionActionEnum.DELETE,
  PermissionActionEnum.MANAGE,
]

/**
 * Module-specific extra actions, grounded in real permission checks:
 * timesheet/expense approval flows, team schedule & earnings approval,
 * project approval, and marketing sends. Extend this map when a new module
 * gains an approval or send flow.
 */
export const MODULE_EXTRA_ACTIONS: Partial<
  Record<ModuleTypeEnum, PermissionActionEnum[]>
> = {
  [ModuleTypeEnum.TIME_ENTRY]: [PermissionActionEnum.APPROVE],
  [ModuleTypeEnum.EXPENSE]: [PermissionActionEnum.APPROVE],
  [ModuleTypeEnum.TEAM]: [PermissionActionEnum.APPROVE],
  [ModuleTypeEnum.PROJECT]: [PermissionActionEnum.APPROVE],
  [ModuleTypeEnum.MARKETING]: [PermissionActionEnum.SEND],
  // Earnings approval (payroll + contractor invoices) lives in Profitability.
  [ModuleTypeEnum.FINANCIALS]: [PermissionActionEnum.APPROVE],
  // Contract approval gate (contract.settings.requireApproval).
  [ModuleTypeEnum.CONTRACT]: [PermissionActionEnum.APPROVE],
}

/** The full set of actions that are meaningful for a module. */
export function getAllowedActionsForModule(
  moduleType: ModuleTypeEnum,
): PermissionActionEnum[] {
  return [...BASE_MODULE_ACTIONS, ...(MODULE_EXTRA_ACTIONS[moduleType] ?? [])]
}

export type ModulePermissionMode = 'tiered' | 'binary' | 'view-full'

/**
 * Controls which grant shapes a module exposes in permission sets.
 * Binary modules deliberately collapse every meaningful action into one
 * access toggle; My Organization separates safe viewing from administration.
 */
export const MODULE_PERMISSION_MODES: Partial<
  Record<ModuleTypeEnum, ModulePermissionMode>
> = {
  [ModuleTypeEnum.AI]: 'binary',
  [ModuleTypeEnum.CUSTOM_FIELD]: 'binary',
  [ModuleTypeEnum.INTEGRATION]: 'binary',
  [ModuleTypeEnum.ORGANIZATION]: 'view-full',
}

export function getModulePermissionMode(
  moduleType: ModuleTypeEnum,
): ModulePermissionMode {
  return MODULE_PERMISSION_MODES[moduleType] ?? 'tiered'
}

/** Module types that remain valid domain records but never appear in sets. */
export const MODULE_TYPES_WITHOUT_PERMISSION_DEFINITIONS = [
  ModuleTypeEnum.LEAD,
  ModuleTypeEnum.TODAY,
  ModuleTypeEnum.MEMBER,
] as const

export function moduleUsesPermissionDefinitions(
  moduleType: ModuleTypeEnum,
): boolean {
  return !(MODULE_TYPES_WITHOUT_PERMISSION_DEFINITIONS as readonly ModuleTypeEnum[]).includes(
    moduleType,
  )
}

/**
 * Error codes for permission operations
 */
export const PermissionErrorCode = {
  NOT_FOUND: 'PERMISSION_NOT_FOUND',
  UNAUTHORIZED: 'UNAUTHORIZED_ACCESS',
  INVALID_ASSIGNMENT: 'INVALID_PERMISSION_ASSIGNMENT',
  SERVER_ERROR: 'PERMISSION_SERVER_ERROR',
} as const

export type PermissionErrorCode =
  (typeof PermissionErrorCode)[keyof typeof PermissionErrorCode]

/**
 * Permission definition schema
 */
export const PermissionDefinitionSchema = z.object({
  id: z.string().uuid().optional(),
  organizationId: z.string().uuid().optional(),
  name: z.string(),
  description: z.string().nullable().optional(),
  moduleType: z.nativeEnum(ModuleTypeEnum),
  allowedActions: z.array(z.nativeEnum(PermissionActionEnum)),
  isSystem: z.boolean().default(false),
  isActive: z.boolean().default(true),
  createdAt: z.date().optional(),
  updatedAt: z.date().optional(),
})

export type PermissionDefinition = z.infer<typeof PermissionDefinitionSchema>

/**
 * Permission set schema
 */
export const PermissionSetSchema = z.object({
  id: z.string().uuid().optional(),
  organizationId: z.string().uuid().optional(),
  name: z.string(),
  description: z.string().nullable().optional(),
  systemKey: z.string().nullable().optional(),
  isSystem: z.boolean().default(false),
  isActive: z.boolean().default(true),
  createdAt: z.date().optional(),
  updatedAt: z.date().optional(),
})

export type PermissionSet = z.infer<typeof PermissionSetSchema>

/**
 * Permission set item schema
 */
export const PermissionSetItemSchema = z.object({
  id: z.string().uuid().optional(),
  permissionSetId: z.string().uuid(),
  permissionDefinitionId: z.string().uuid(),
  createdAt: z.date().optional(),
})

export type PermissionSetItem = z.infer<typeof PermissionSetItemSchema>

/**
 * Permission assignment schema (individual permissions to members)
 */
export const PermissionAssignmentSchema = z.object({
  id: z.string().uuid().optional(),
  memberId: z.string().uuid(),
  permissionDefinitionId: z.string().uuid(),
  resourceId: z.string().uuid().nullable().optional(),
  resourceType: z.nativeEnum(ModuleTypeEnum).nullable().optional(),
  createdAt: z.date().optional(),
  updatedAt: z.date().optional(),
})

export type PermissionAssignment = z.infer<typeof PermissionAssignmentSchema>

/**
 * Permission set assignment schema
 */
export const PermissionSetAssignmentSchema = z.object({
  id: z.string().uuid().optional(),
  memberId: z.string().uuid(),
  permissionSetId: z.string().uuid(),
  createdAt: z.date().optional(),
  updatedAt: z.date().optional(),
})

export type PermissionSetAssignment = z.infer<
  typeof PermissionSetAssignmentSchema
>

/**
 * DTOs for permission operations
 */

export const CreatePermissionDefinitionDtoSchema = z.object({
  name: z.string(),
  description: z.string().optional(),
  moduleType: z.nativeEnum(ModuleTypeEnum),
  allowedActions: z.array(z.nativeEnum(PermissionActionEnum)),
  isSystem: z.boolean().optional().default(false),
  isActive: z.boolean().optional().default(true),
})

export type CreatePermissionDefinitionDto = z.infer<
  typeof CreatePermissionDefinitionDtoSchema
>

export const UpdatePermissionDefinitionDtoSchema = z.object({
  name: z.string().optional(),
  description: z.string().optional(),
  moduleType: z.nativeEnum(ModuleTypeEnum).optional(),
  allowedActions: z.array(z.nativeEnum(PermissionActionEnum)).optional(),
  isActive: z.boolean().optional(),
})

export type UpdatePermissionDefinitionDto = z.infer<
  typeof UpdatePermissionDefinitionDtoSchema
>

export const CreatePermissionSetDtoSchema = z.object({
  name: z.string(),
  description: z.string().optional(),
  permissionIds: z.array(z.string().uuid()),
  isSystem: z.boolean().optional().default(false),
  isActive: z.boolean().optional().default(true),
})

export type CreatePermissionSetDto = z.infer<
  typeof CreatePermissionSetDtoSchema
>

export const UpdatePermissionSetDtoSchema = z.object({
  name: z.string().optional(),
  description: z.string().optional(),
  permissionIds: z.array(z.string().uuid()).optional(),
  isActive: z.boolean().optional(),
})

export type UpdatePermissionSetDto = z.infer<
  typeof UpdatePermissionSetDtoSchema
>

export const AssignPermissionDtoSchema = z.object({
  memberId: z.string().uuid(),
  permissionDefinitionId: z.string().uuid(),
  resourceId: z.string().uuid().optional(),
  resourceType: z.nativeEnum(ModuleTypeEnum).optional(),
})

export type AssignPermissionDto = z.infer<typeof AssignPermissionDtoSchema>

export const AssignPermissionSetDtoSchema = z.object({
  memberId: z.string().uuid(),
  permissionSetId: z.string().uuid(),
})

export type AssignPermissionSetDto = z.infer<
  typeof AssignPermissionSetDtoSchema
>

/**
 * Helper function to check if a permission includes a specific action
 */
export function hasPermissionAction(
  permissions: PermissionDefinition[],
  moduleType: ModuleTypeEnum,
  action: PermissionActionEnum,
): boolean {
  return permissions.some(
    p =>
      p.moduleType === moduleType &&
      p.allowedActions.includes(action) &&
      p.isActive,
  )
}

/**
 * Utility to create a full permission set for a module (all actions)
 */
export function createFullModulePermission(
  moduleType: ModuleTypeEnum,
  namePrefix: string = '',
): PermissionDefinition {
  if (moduleType === ModuleTypeEnum.LEAD) {
    throw new Error('Lead permissions are retired; use CONTACT instead')
  }
  if (moduleType === ModuleTypeEnum.TODAY) {
    throw new Error(
      'Today does not use module permissions; organization membership grants access',
    )
  }
  if (moduleType === ModuleTypeEnum.MEMBER) {
    throw new Error(
      'Member access is role-based; use TEAM for workforce permissions',
    )
  }
  // Get user-friendly module name based on the module type
  let moduleName
  switch (moduleType) {
    case ModuleTypeEnum.PROJECT:
      moduleName = 'Projects'
      break
    case ModuleTypeEnum.TASK:
      moduleName = 'Tasks'
      break
    case ModuleTypeEnum.TICKET:
      moduleName = 'Tickets'
      break
    case ModuleTypeEnum.KNOWLEDGE_BASE:
      moduleName = 'Knowledge Base'
      break
    case ModuleTypeEnum.ENTITY:
      moduleName = 'Entities'
      break
    case ModuleTypeEnum.DEAL:
      moduleName = 'Deals'
      break
    case ModuleTypeEnum.EVENT:
      moduleName = 'Events'
      break
    case ModuleTypeEnum.CONTRACT:
      moduleName = 'Contracts'
      break
    case ModuleTypeEnum.INVOICE:
      moduleName = 'Invoices'
      break
    case ModuleTypeEnum.QUOTE:
      moduleName = 'Quotes'
      break
    case ModuleTypeEnum.BILLING_AUTHORIZATION:
      moduleName = 'Billing Authorizations'
      break
    case ModuleTypeEnum.EXPENSE:
      moduleName = 'Expenses'
      break
    case ModuleTypeEnum.THREAD:
      moduleName = 'Discussions'
      break
    case ModuleTypeEnum.TIME_ENTRY:
      moduleName = 'Timesheets'
      break
    case ModuleTypeEnum.CUSTOM_COLLECTION:
      moduleName = 'Collections'
      break
    case ModuleTypeEnum.CUSTOM_FIELD:
      moduleName = 'Custom Fields'
      break
    case ModuleTypeEnum.TEAM:
      moduleName = 'Team'
      break
    case ModuleTypeEnum.INVENTORY:
      moduleName = 'Inventory'
      break
    case ModuleTypeEnum.SUPPLIER:
      moduleName = 'Suppliers'
      break
    case ModuleTypeEnum.DOCUMENT:
      moduleName = 'Documents'
      break
    case ModuleTypeEnum.NOTE:
      moduleName = 'Notes'
      break
    case ModuleTypeEnum.CONTACT:
      moduleName = 'Contacts'
      break
    case ModuleTypeEnum.CALENDAR:
      moduleName = 'Calendar'
      break
    case ModuleTypeEnum.FORM:
      moduleName = 'Forms'
      break
    case ModuleTypeEnum.WORKFLOW:
      moduleName = 'Workflows'
      break
    case ModuleTypeEnum.ORGANIZATION:
      moduleName = 'My Organization'
      break
    case ModuleTypeEnum.ANALYTICS:
      moduleName = 'Analytics'
      break
    case ModuleTypeEnum.AI:
      moduleName = 'AI'
      break
    case ModuleTypeEnum.WORKSTREAM:
      moduleName = 'Workstreams'
      break
    case ModuleTypeEnum.INTEGRATION:
      moduleName = 'Integrations'
      break
    case ModuleTypeEnum.MARKETING:
      moduleName = 'Marketing'
      break
    case ModuleTypeEnum.FINANCIALS:
      moduleName = 'Profitability'
      break
    default:
      moduleName = String(moduleType)
  }

  return {
    id: `${moduleType.toLowerCase()}_full_access`,
    name: `${namePrefix}${namePrefix ? ' ' : ''}${moduleName} - Full Access`,
    description: `Full access to all ${moduleName.toLowerCase()} features`,
    moduleType: moduleType,
    allowedActions: getAllowedActionsForModule(moduleType),
    isSystem: true,
    isActive: true,
  }
}

/** One yes/no access grant containing every action the module enforces. */
export function createModuleAccessPermission(
  moduleType: ModuleTypeEnum,
): PermissionDefinition {
  const fullPermission = createFullModulePermission(moduleType)
  const moduleName = fullPermission.name.replace(/ - Full Access$/, '')

  return {
    ...fullPermission,
    id: `${moduleType.toLowerCase()}_access`,
    name: `${moduleName} - Access`,
    description: `Access ${moduleName.toLowerCase()} features`,
  }
}

/** Read-only module visibility without administrative capabilities. */
export function createModuleViewPermission(
  moduleType: ModuleTypeEnum,
): PermissionDefinition {
  const fullPermission = createFullModulePermission(moduleType)
  const moduleName = fullPermission.name.replace(/ - Full Access$/, '')

  return {
    ...fullPermission,
    id: `${moduleType.toLowerCase()}_view`,
    name: `${moduleName} - View`,
    description: `View ${moduleName.toLowerCase()} information`,
    allowedActions: [PermissionActionEnum.READ],
  }
}

/**
 * Utility to create permissions for a Standard Access level
 * Includes access to core collaboration modules
 */
export function createRegularUserPermission(
  moduleType: ModuleTypeEnum,
): PermissionDefinition {
  if (moduleType === ModuleTypeEnum.LEAD) {
    throw new Error('Lead permissions are retired; use CONTACT instead')
  }
  if (moduleType === ModuleTypeEnum.TODAY) {
    throw new Error(
      'Today does not use module permissions; organization membership grants access',
    )
  }
  if (moduleType === ModuleTypeEnum.MEMBER) {
    throw new Error(
      'Member access is role-based; use TEAM for workforce permissions',
    )
  }
  // Get user-friendly module name based on the module type
  let moduleName
  switch (moduleType) {
    case ModuleTypeEnum.PROJECT:
      moduleName = 'Projects'
      break
    case ModuleTypeEnum.TASK:
      moduleName = 'Tasks'
      break
    case ModuleTypeEnum.TICKET:
      moduleName = 'Tickets'
      break
    case ModuleTypeEnum.KNOWLEDGE_BASE:
      moduleName = 'Knowledge Base'
      break
    case ModuleTypeEnum.ENTITY:
      moduleName = 'Entities'
      break
    case ModuleTypeEnum.TIME_ENTRY:
      moduleName = 'Timesheets'
      break
    case ModuleTypeEnum.INVENTORY:
      moduleName = 'Inventory'
      break
    case ModuleTypeEnum.SUPPLIER:
      moduleName = 'Suppliers'
      break
    case ModuleTypeEnum.EXPENSE:
      moduleName = 'Expenses'
      break
    case ModuleTypeEnum.THREAD:
      moduleName = 'Discussions'
      break
    case ModuleTypeEnum.NOTE:
      moduleName = 'Notes'
      break
    case ModuleTypeEnum.CONTACT:
      moduleName = 'Contacts'
      break
    case ModuleTypeEnum.CALENDAR:
      moduleName = 'Calendar'
      break
    case ModuleTypeEnum.CUSTOM_COLLECTION:
      moduleName = 'Collections'
      break
    case ModuleTypeEnum.CUSTOM_FIELD:
      moduleName = 'Custom Fields'
      break
    case ModuleTypeEnum.DOCUMENT:
      moduleName = 'Documents'
      break
    case ModuleTypeEnum.FORM:
      moduleName = 'Forms'
      break
    case ModuleTypeEnum.WORKFLOW:
      moduleName = 'Workflows'
      break
    case ModuleTypeEnum.ORGANIZATION:
      moduleName = 'My Organization'
      break
    case ModuleTypeEnum.INTEGRATION:
      moduleName = 'Integrations'
      break
    case ModuleTypeEnum.EVENT:
      moduleName = 'Events'
      break
    case ModuleTypeEnum.DEAL:
      moduleName = 'Deals'
      break
    case ModuleTypeEnum.INVOICE:
      moduleName = 'Invoices'
      break
    case ModuleTypeEnum.QUOTE:
      moduleName = 'Quotes'
      break
    case ModuleTypeEnum.CONTRACT:
      moduleName = 'Contracts'
      break
    case ModuleTypeEnum.TEAM:
      moduleName = 'Team'
      break
    case ModuleTypeEnum.ANALYTICS:
      moduleName = 'Analytics'
      break
    case ModuleTypeEnum.AI:
      moduleName = 'AI'
      break
    case ModuleTypeEnum.WORKSTREAM:
      moduleName = 'Workstreams'
      break
    case ModuleTypeEnum.MARKETING:
      moduleName = 'Marketing'
      break
    case ModuleTypeEnum.FINANCIALS:
      moduleName = 'Profitability'
      break
    default:
      return null as any // Don't create permissions for other modules
  }

  // Rule: "Edit" (Standard Access) = read, create, update. DELETE belongs to "Full".
  // Applied so far to modules whose delete is a permanent cascade (projects,
  // entities). Modules whose delete is a soft delete of own work (tasks, notes,
  // documents) and the remaining list still carry DELETE pending review.
  let allowedActions = [
    PermissionActionEnum.READ,
    PermissionActionEnum.CREATE,
    PermissionActionEnum.UPDATE,
    PermissionActionEnum.DELETE,
  ]

  if (
    moduleType === ModuleTypeEnum.PROJECT ||
    moduleType === ModuleTypeEnum.ENTITY
  ) {
    allowedActions = [
      PermissionActionEnum.READ,
      PermissionActionEnum.CREATE,
      PermissionActionEnum.UPDATE,
    ]
  }

  if (moduleType === ModuleTypeEnum.CALENDAR) {
    allowedActions = [
      PermissionActionEnum.READ,
      PermissionActionEnum.CREATE,
      PermissionActionEnum.UPDATE,
    ]
  }

  if (moduleType === ModuleTypeEnum.ORGANIZATION) {
    allowedActions = [PermissionActionEnum.READ]
  }

  // Add APPROVE action for tasks (can approve their own tasks)
  if (moduleType === ModuleTypeEnum.TASK) {
    allowedActions.push(PermissionActionEnum.APPROVE)
  }

  let description = `Standard access to ${moduleName.toLowerCase()} with ability to create, view, update, and delete records`

  if (
    moduleType === ModuleTypeEnum.PROJECT ||
    moduleType === ModuleTypeEnum.ENTITY
  ) {
    description = `Standard access to ${moduleName.toLowerCase()} with ability to create, view, and update records; deleting requires full access`
  }

  if (moduleType === ModuleTypeEnum.KNOWLEDGE_BASE) {
    allowedActions = [
      PermissionActionEnum.READ,
      PermissionActionEnum.CREATE,
      PermissionActionEnum.UPDATE,
    ]
    description =
      'Read and author knowledge; publishing and delivery settings require full access'
  }

  if (moduleType === ModuleTypeEnum.CALENDAR) {
    description =
      'Standard access to calendar with ability to view, create, and update entries'
  }

  if (moduleType === ModuleTypeEnum.ORGANIZATION) {
    description = 'View organization information without changing settings'
  }

  if (moduleType === ModuleTypeEnum.AI) {
    allowedActions = [PermissionActionEnum.READ]
    description = 'Standard access to AI assistant features'
  }

  if (moduleType === ModuleTypeEnum.WORKSTREAM) {
    allowedActions = [PermissionActionEnum.READ]
    description = 'Standard access to workstreams you belong to'
  }

  if (moduleType === ModuleTypeEnum.MARKETING) {
    allowedActions = [
      PermissionActionEnum.READ,
      PermissionActionEnum.CREATE,
      PermissionActionEnum.UPDATE,
    ]
    description =
      'Create and edit marketing content without permission to send, approve, or manage compliance settings'
  }

  if (moduleType === ModuleTypeEnum.FINANCIALS) {
    allowedActions = [PermissionActionEnum.READ]
    description =
      'View profitability, forecast, and rates without changing rate configuration or approving pay'
  }

  return {
    id: `standard_access_${moduleType.toLowerCase()}`,
    name: `${moduleName} - Standard Access`,
    description,
    moduleType: moduleType,
    allowedActions,
    isSystem: true,
    isActive: true,
  }
}

/**
 * Utility to create permissions for a Full Admin access level
 * Full administrative access to all modules
 */
export function createFullAdminPermission(
  moduleType: ModuleTypeEnum,
): PermissionDefinition {
  if (moduleType === ModuleTypeEnum.LEAD) {
    throw new Error('Lead permissions are retired; use CONTACT instead')
  }
  if (moduleType === ModuleTypeEnum.TODAY) {
    throw new Error(
      'Today does not use module permissions; organization membership grants access',
    )
  }
  if (moduleType === ModuleTypeEnum.MEMBER) {
    throw new Error(
      'Member access is role-based; use TEAM for workforce permissions',
    )
  }
  // Get user-friendly module name based on the module type
  let moduleName
  switch (moduleType) {
    case ModuleTypeEnum.PROJECT:
      moduleName = 'Projects'
      break
    case ModuleTypeEnum.TASK:
      moduleName = 'Tasks'
      break
    case ModuleTypeEnum.TICKET:
      moduleName = 'Tickets'
      break
    case ModuleTypeEnum.KNOWLEDGE_BASE:
      moduleName = 'Knowledge Base'
      break
    case ModuleTypeEnum.ENTITY:
      moduleName = 'Entities'
      break
    case ModuleTypeEnum.DEAL:
      moduleName = 'Deals'
      break
    case ModuleTypeEnum.EVENT:
      moduleName = 'Events'
      break
    case ModuleTypeEnum.CONTRACT:
      moduleName = 'Contracts'
      break
    case ModuleTypeEnum.INVOICE:
      moduleName = 'Invoices'
      break
    case ModuleTypeEnum.QUOTE:
      moduleName = 'Quotes'
      break
    case ModuleTypeEnum.BILLING_AUTHORIZATION:
      moduleName = 'Billing Authorizations'
      break
    case ModuleTypeEnum.EXPENSE:
      moduleName = 'Expenses'
      break
    case ModuleTypeEnum.THREAD:
      moduleName = 'Discussions'
      break
    case ModuleTypeEnum.TIME_ENTRY:
      moduleName = 'Timesheets'
      break
    case ModuleTypeEnum.CUSTOM_COLLECTION:
      moduleName = 'Collections'
      break
    case ModuleTypeEnum.TEAM:
      moduleName = 'Team'
      break
    case ModuleTypeEnum.INVENTORY:
      moduleName = 'Inventory'
      break
    case ModuleTypeEnum.SUPPLIER:
      moduleName = 'Suppliers'
      break
    case ModuleTypeEnum.DOCUMENT:
      moduleName = 'Documents'
      break
    case ModuleTypeEnum.NOTE:
      moduleName = 'Notes'
      break
    case ModuleTypeEnum.CONTACT:
      moduleName = 'Contacts'
      break
    case ModuleTypeEnum.CALENDAR:
      moduleName = 'Calendar'
      break
    case ModuleTypeEnum.FORM:
      moduleName = 'Forms'
      break
    case ModuleTypeEnum.WORKFLOW:
      moduleName = 'Workflows'
      break
    case ModuleTypeEnum.ORGANIZATION:
      moduleName = 'My Organization'
      break
    case ModuleTypeEnum.ANALYTICS:
      moduleName = 'Analytics'
      break
    case ModuleTypeEnum.WORKSTREAM:
      moduleName = 'Workstreams'
      break
    case ModuleTypeEnum.FINANCIALS:
      moduleName = 'Profitability'
      break
    default:
      moduleName = String(moduleType)
  }

  return {
    id: `full_admin_${moduleType.toLowerCase()}`,
    name: `Full Admin - ${moduleName}`,
    description: `Full administrative access to all ${moduleName.toLowerCase()} features including management capabilities`,
    moduleType: moduleType,
    allowedActions: getAllowedActionsForModule(moduleType),
    isSystem: true,
    isActive: true,
  }
}
